/**
 * Printer Service
 *
 * Main service for managing printer connections and printing receipts.
 * Automatically selects the appropriate adapter based on platform.
 */

import { Capacitor } from '@capacitor/core'
import type {
  PrinterAdapter,
  PrinterConnectionType,
  PrinterDevice,
  PrinterState,
  ReceiptData,
} from './types'
import { WebBluetoothAdapter } from './adapters/webBluetooth'
import { WebUsbAdapter } from './adapters/webUsb'
import { CapacitorBluetoothAdapter } from './adapters/capacitor'
import { buildReceipt, buildTestReceipt } from './escpos'

type StateListener = (state: PrinterState) => void
type AdapterPreference = PrinterConnectionType | 'auto'

class PrinterService {
  private adapter: PrinterAdapter | null = null
  private listeners: Set<StateListener> = new Set()
  private preferredType: AdapterPreference = 'auto'
  private state: PrinterState = {
    status: 'disconnected',
    device: null,
    error: null,
    isScanning: false,
    availableDevices: [],
  }

  constructor() {
    this.initializeAdapter()
  }

  /**
   * Initialize the appropriate adapter based on platform
   */
  private initializeAdapter(): void {
    const preferredType = this.preferredType

    if (preferredType === 'system') {
      this.adapter = null
      return
    }

    const supportsWebBluetooth = WebBluetoothAdapter.isSupported()
    const supportsWebUsb = WebUsbAdapter.isSupported()
    const supportsNative = Capacitor.isNativePlatform() && CapacitorBluetoothAdapter.isPluginAvailable()

    if (preferredType === 'usb') {
      if (supportsWebUsb) {
        console.log('[PrinterService] Using WebUSB adapter')
        this.adapter = new WebUsbAdapter()
        return
      }
    }

    if (preferredType === 'bluetooth') {
      if (supportsNative) {
        console.log('[PrinterService] Using Capacitor native adapter')
        this.adapter = new CapacitorBluetoothAdapter()
        return
      }
      if (supportsWebBluetooth) {
        console.log('[PrinterService] Using Web Bluetooth adapter')
        this.adapter = new WebBluetoothAdapter()
        return
      }
    }

    if (supportsNative) {
      console.log('[PrinterService] Using Capacitor native adapter')
      this.adapter = new CapacitorBluetoothAdapter()
      return
    }

    if (supportsWebBluetooth) {
      console.log('[PrinterService] Using Web Bluetooth adapter')
      this.adapter = new WebBluetoothAdapter()
      return
    }

    if (supportsWebUsb) {
      console.log('[PrinterService] Using WebUSB adapter')
      this.adapter = new WebUsbAdapter()
      return
    }

    console.warn('[PrinterService] No printer adapter available')
    this.adapter = null
  }

  /**
   * Set preferred connection type and reinitialize adapter
   */
  setPreferredType(type: AdapterPreference): void {
    this.preferredType = type
    this.initializeAdapter()
    this.setState({
      status: 'disconnected',
      device: null,
      error: null,
      isScanning: false,
      availableDevices: [],
    })
  }

  getPreferredType(): AdapterPreference {
    return this.preferredType
  }

  /**
   * Check if printing is supported on this device
   */
  isSupported(): boolean {
    if (this.preferredType === 'system') return true
    return this.adapter !== null
  }

  /**
   * Get the current platform info for display
   */
  getPlatformInfo(): { platform: string; adapterType: string; supported: boolean } {
    const platform = Capacitor.isNativePlatform()
      ? Capacitor.getPlatform()
      : 'web'

    let adapterType = 'none'
    if (this.preferredType === 'system') {
      adapterType = 'system'
    }
    if (this.adapter instanceof CapacitorBluetoothAdapter) {
      adapterType = 'native'
    } else if (this.adapter instanceof WebBluetoothAdapter) {
      adapterType = 'webBluetooth'
    } else if (this.adapter instanceof WebUsbAdapter) {
      adapterType = 'webUsb'
    }

    return {
      platform,
      adapterType,
      supported: this.isSupported(),
    }
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener)
    listener(this.getState())

    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Get current state
   */
  getState(): PrinterState {
    return { ...this.state }
  }

  /**
   * Update state and notify listeners
   */
  private setState(updates: Partial<PrinterState>): void {
    this.state = { ...this.state, ...updates }
    this.listeners.forEach(listener => listener(this.getState()))
  }

  /**
   * Scan for available printers
   */
  async scan(): Promise<PrinterDevice[]> {
    if (!this.adapter) {
      this.setState({ error: 'Printing not supported on this device' })
      return []
    }

    this.setState({ isScanning: true, error: null })

    try {
      const devices = await this.adapter.scan()
      this.setState({
        isScanning: false,
        availableDevices: devices,
      })
      return devices
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Scan failed'
      this.setState({ isScanning: false, error: errorMessage })
      return []
    }
  }

  /**
   * Connect to a printer
   */
  async connect(deviceId: string): Promise<boolean> {
    if (!this.adapter) {
      this.setState({ error: 'Printing not supported' })
      return false
    }

    this.setState({ status: 'connecting', error: null })

    try {
      const success = await this.adapter.connect(deviceId)

      if (success) {
        const device = this.adapter.getConnectedDevice()
        this.setState({
          status: 'connected',
          device,
        })
        return true
      }

      this.setState({
        status: 'disconnected',
        error: 'Failed to connect',
      })
      return false
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Connection failed'
      this.setState({
        status: 'error',
        error: errorMessage,
      })
      return false
    }
  }

  /**
   * Disconnect from current printer
   */
  async disconnect(): Promise<void> {
    if (!this.adapter) return

    try {
      await this.adapter.disconnect()
      this.setState({
        status: 'disconnected',
        device: null,
      })
    } catch (error) {
      console.error('[PrinterService] Disconnect error:', error)
    }
  }

  /**
   * Check connection status
   */
  async checkConnection(): Promise<boolean> {
    if (!this.adapter) return false

    const connected = await this.adapter.isConnected()
    const device = this.adapter.getConnectedDevice()

    this.setState({
      status: connected ? 'connected' : 'disconnected',
      device: connected ? device : null,
    })

    return connected
  }

  /**
   * Print a receipt
   */
  async printReceipt(data: ReceiptData): Promise<boolean> {
    if (!this.adapter) {
      this.setState({ error: 'Printing not supported' })
      return false
    }

    const isConnected = await this.adapter.isConnected()
    if (!isConnected) {
      this.setState({ error: 'Printer not connected' })
      return false
    }

    this.setState({ status: 'printing', error: null })

    try {
      const receiptData = buildReceipt(data)
      const success = await this.adapter.print(receiptData)

      this.setState({
        status: success ? 'connected' : 'error',
        error: success ? null : 'Print failed',
      })

      return success
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Print failed'
      this.setState({
        status: 'error',
        error: errorMessage,
      })
      return false
    }
  }

  /**
   * Print a test receipt
   */
  async printTestReceipt(): Promise<boolean> {
    if (!this.adapter) {
      this.setState({ error: 'Printing not supported' })
      return false
    }

    const isConnected = await this.adapter.isConnected()
    if (!isConnected) {
      this.setState({ error: 'Printer not connected' })
      return false
    }

    this.setState({ status: 'printing', error: null })

    try {
      const testData = buildTestReceipt()
      const success = await this.adapter.print(testData)

      this.setState({
        status: success ? 'connected' : 'error',
        error: success ? null : 'Test print failed',
      })

      return success
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Test print failed'
      this.setState({
        status: 'error',
        error: errorMessage,
      })
      return false
    }
  }

  /**
   * Send raw ESC/POS data to printer
   */
  async printRaw(data: Uint8Array): Promise<boolean> {
    if (!this.adapter) {
      this.setState({ error: 'Printing not supported' })
      return false
    }

    const isConnected = await this.adapter.isConnected()
    if (!isConnected) {
      this.setState({ error: 'Printer not connected' })
      return false
    }

    try {
      return await this.adapter.print(data)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Print failed'
      this.setState({ error: errorMessage })
      return false
    }
  }
}

export const printerService = new PrinterService()
export { PrinterService }
